import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

// ─── Template definitions (mirrors backend) ───────────────────────────────────

const TEMPLATES = [
  {
    key: 'cold_value',
    label: 'Cold Outreach — Value-first',
    tag: 'B2B Prospect',
    tagColor: 'blue',
    trigger: 'First contact',
    recipientType: 'cold_value',
    description: 'Sent to new prospects. Positions TeamGrid as non-surveillance workforce intelligence.',
    globalVars: [
      { name: 'calendarLink', label: 'Calendly Link', placeholder: 'https://calendly.com/...', required: true },
      { name: 'senderName', label: 'Sender Name', placeholder: 'TeamGrid Partnerships' },
      { name: 'senderPhone', label: 'Sender Phone', placeholder: '+91 ...' },
    ],
    recipientVars: [
      { name: 'name', label: 'Contact Name', required: true },
      { name: 'companyName', label: 'Company Name', required: true },
      { name: 'email', label: 'Email', required: true },
    ],
    manualRecipients: true,
  },
  {
    key: 'cold_roi',
    label: 'Cold Outreach — ROI-led',
    tag: 'B2B Prospect',
    tagColor: 'blue',
    trigger: '5 days after Value-first (no reply)',
    recipientType: 'cold_roi',
    description: 'Follow-up to cold_value. Leads with ROI metrics.',
    globalVars: [
      { name: 'calendarLink', label: 'Calendly Link', placeholder: 'https://calendly.com/...', required: true },
      { name: 'senderName', label: 'Sender Name', placeholder: 'TeamGrid Partnerships' },
      { name: 'senderPhone', label: 'Sender Phone', placeholder: '+91 ...' },
    ],
    recipientVars: [
      { name: 'name', label: 'Contact Name', required: true },
      { name: 'companyName', label: 'Company Name', required: true },
      { name: 'email', label: 'Email', required: true },
    ],
    manualRecipients: true,
  },
  {
    key: 'welcome',
    label: 'Welcome / Onboarding',
    tag: 'All New Users',
    tagColor: 'green',
    trigger: 'Immediately after signup',
    recipientType: 'welcome',
    description: 'Sent to new signups (last 7 days). Walks them through first steps.',
    globalVars: [
      { name: 'calendarLink', label: 'Calendly Link', placeholder: 'https://calendly.com/...', required: true },
    ],
    recipientVars: [],
    manualRecipients: false,
  },
  {
    key: 'activate_features',
    label: 'Activate Unused Features',
    tag: 'Existing B2B',
    tagColor: 'amber',
    trigger: 'Monthly',
    recipientType: 'activate_features',
    description: 'Sent monthly to B2B partners who haven\'t explored certain features.',
    globalVars: [
      { name: 'feature1', label: 'Feature 1 Name', placeholder: 'AI Recommendations', required: true },
      { name: 'feature1Desc', label: 'Feature 1 Description', placeholder: 'Know what to act on, not just what happened', required: true },
      { name: 'feature2', label: 'Feature 2 Name', placeholder: 'Ask TG AI', required: true },
      { name: 'feature2Desc', label: 'Feature 2 Description', placeholder: 'Ask any workforce question in plain language', required: true },
      { name: 'calendarLink', label: 'Calendly Link', placeholder: 'https://calendly.com/...', required: true },
      { name: 'accountManagerName', label: 'Account Manager Name', placeholder: 'TeamGrid Customer Success' },
      { name: 'accountManagerPhone', label: 'Account Manager Phone', placeholder: '+91 ...' },
    ],
    recipientVars: [],
    manualRecipients: false,
  },
  {
    key: 'quarterly_review',
    label: 'Quarterly Review + Roadmap',
    tag: 'Existing B2B',
    tagColor: 'amber',
    trigger: 'End of each quarter',
    recipientType: 'quarterly_review',
    description: 'QBR email with real usage stats + upcoming features. Drives renewal conversation.',
    globalVars: [
      { name: 'quarter', label: 'Quarter (e.g. 2)', placeholder: '2', required: true },
      { name: 'featureAName', label: 'Upcoming Feature A', placeholder: 'Dashboard V2', required: true },
      { name: 'featureAImpact', label: 'Feature A Impact', placeholder: '1-line impact', required: true },
      { name: 'featureBName', label: 'Upcoming Feature B', placeholder: 'CRM Integration', required: true },
      { name: 'featureBImpact', label: 'Feature B Impact', placeholder: '1-line impact', required: true },
      { name: 'calendarLink', label: 'Calendly Link', placeholder: 'https://calendly.com/...', required: true },
      { name: 'senderName', label: 'Sender Name', placeholder: 'TeamGrid Partnerships' },
      { name: 'senderPhone', label: 'Sender Phone', placeholder: '+91 ...' },
    ],
    recipientVars: [
      { name: 'activeUsers', label: 'Active Users', placeholder: '12' },
      { name: 'insightsGenerated', label: 'Insights Generated', placeholder: '240' },
      { name: 'issuesDetected', label: 'Issues Detected', placeholder: '8' },
      { name: 'aiActedOn', label: 'AI Recommendations Acted On', placeholder: '15' },
    ],
    manualRecipients: false,
  },
  {
    key: 'reengagement',
    label: 'Re-engagement',
    tag: 'Inactive Users',
    tagColor: 'red',
    trigger: '30+ days inactive',
    recipientType: 'reengagement',
    description: 'Sent to users who haven\'t logged in for 30+ days.',
    globalVars: [],
    recipientVars: [],
    manualRecipients: false,
  },
  {
    key: 'feature_launch',
    label: 'Feature Launch — Unified',
    tag: 'Everyone',
    tagColor: 'purple',
    trigger: 'Every release',
    recipientType: 'feature_launch',
    description: 'Single send to all users on every new feature/module release.',
    globalVars: [
      { name: 'featureName', label: 'Feature Name', placeholder: 'Dashboard V2', required: true },
      { name: 'benefit1', label: 'Benefit 1', placeholder: 'Real-time org analytics', required: true },
      { name: 'benefit2', label: 'Benefit 2', placeholder: 'Ask TG AI for instant answers', required: true },
      { name: 'benefit3', label: 'Benefit 3', placeholder: 'Setup in under 5 minutes', required: true },
    ],
    recipientVars: [],
    manualRecipients: false,
  },
  {
    key: 'renewal_14',
    label: 'Renewal Reminder — 14 days',
    tag: 'Billing',
    tagColor: 'orange',
    trigger: '14 days before renewal',
    recipientType: 'renewal_14',
    description: 'Auto-fetches orgs renewing in ~14 days. Prompts review/upgrade.',
    globalVars: [
      { name: 'calendarLink', label: 'Calendly Link', placeholder: 'https://calendly.com/...' },
    ],
    recipientVars: [],
    manualRecipients: false,
  },
  {
    key: 'renewal_2',
    label: 'Renewal Reminder — 2 days',
    tag: 'Billing',
    tagColor: 'orange',
    trigger: '2 days before renewal',
    recipientType: 'renewal_2',
    description: 'Final nudge before auto-renewal fires.',
    globalVars: [
      { name: 'calendarLink', label: 'Calendly Link', placeholder: 'https://calendly.com/...' },
    ],
    recipientVars: [],
    manualRecipients: false,
  },
  {
    key: 'winback',
    label: 'Win-back (Churned)',
    tag: 'Churned B2B',
    tagColor: 'gray',
    trigger: 'After churn — one-time',
    recipientType: 'winback',
    description: 'Sent once to churned B2B partners. Showcases what\'s new since they left.',
    globalVars: [
      { name: 'calendarLink', label: 'Calendly Link', placeholder: 'https://calendly.com/...', required: true },
      { name: 'senderName', label: 'Sender Name', placeholder: 'TeamGrid Partnerships' },
      { name: 'senderPhone', label: 'Sender Phone', placeholder: '+91 ...' },
    ],
    recipientVars: [
      { name: 'name', label: 'Contact Name', required: true },
      { name: 'companyName', label: 'Company Name', required: true },
      { name: 'email', label: 'Email', required: true },
    ],
    manualRecipients: true,
  },
];

// ─── Tag colour map ───────────────────────────────────────────────────────────

const TAG_COLORS = {
  blue:   { bg: 'bg-blue-500/15',   text: 'text-blue-300',   border: 'border-blue-500/30' },
  green:  { bg: 'bg-emerald-500/15', text: 'text-emerald-300', border: 'border-emerald-500/30' },
  amber:  { bg: 'bg-amber-500/15',  text: 'text-amber-300',  border: 'border-amber-500/30' },
  red:    { bg: 'bg-red-500/15',    text: 'text-red-300',    border: 'border-red-500/30' },
  purple: { bg: 'bg-purple-500/15', text: 'text-purple-300', border: 'border-purple-500/30' },
  orange: { bg: 'bg-orange-500/15', text: 'text-orange-300', border: 'border-orange-500/30' },
  gray:   { bg: 'bg-gray-500/15',   text: 'text-gray-300',   border: 'border-gray-500/30' },
};

// ─── Sub-components ───────────────────────────────────────────────────────────

function Tag({ color, label }) {
  const c = TAG_COLORS[color] || TAG_COLORS.gray;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${c.bg} ${c.text} ${c.border}`}>
      {label}
    </span>
  );
}

function InputField({ label, name, value, onChange, placeholder, required }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-400 mb-1">
        {label}{required && <span className="text-red-400 ml-1">*</span>}
      </label>
      <input
        type="text"
        value={value}
        onChange={e => onChange(name, e.target.value)}
        placeholder={placeholder}
        className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:border-blue-500/50 focus:bg-white/8 transition-all"
      />
    </div>
  );
}

function RecipientRow({ r, idx, vars, onVarChange, onRemove, recipientVars }) {
  return (
    <div className="flex items-start gap-2 p-3 bg-white/3 rounded-lg border border-white/8 group">
      <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
        <div>
          <span className="text-xs text-gray-400">Email</span>
          <p className="text-sm text-white truncate">{r.email}</p>
        </div>
        <div>
          <span className="text-xs text-gray-400">Name</span>
          <p className="text-sm text-white truncate">{r.name || r.companyName || '—'}</p>
        </div>
        {recipientVars.map(v => (
          <div key={v.name}>
            <label className="text-xs text-gray-400">{v.label}</label>
            <input
              type="text"
              value={vars[idx]?.[v.name] || r[v.name] || ''}
              onChange={e => onVarChange(idx, v.name, e.target.value)}
              placeholder={v.placeholder}
              className="w-full mt-0.5 px-2 py-1 bg-white/5 border border-white/10 rounded text-xs text-white placeholder-gray-500 focus:outline-none focus:border-blue-500/50"
            />
          </div>
        ))}
      </div>
      <button
        onClick={() => onRemove(idx)}
        className="opacity-0 group-hover:opacity-100 p-1 text-red-400 hover:text-red-300 transition-all"
        title="Remove"
      >
        ✕
      </button>
    </div>
  );
}

// ─── Manual prospect CSV parser ───────────────────────────────────────────────
function parseProspectCSV(text) {
  const lines = text.trim().split('\n').filter(Boolean);
  return lines.map(line => {
    const [email = '', name = '', companyName = ''] = line.split(',').map(s => s.trim());
    return { email, name, companyName };
  }).filter(r => r.email.includes('@'));
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function EmailCampaigns({ isDark }) {
  const [selectedKey, setSelectedKey] = useState(null);
  const [status, setStatus] = useState(null); // { dryRun, templates }
  const [recipients, setRecipients] = useState([]);
  const [recipientVarOverrides, setRecipientVarOverrides] = useState({});
  const [globalVars, setGlobalVars] = useState({});
  const [loadingRecipients, setLoadingRecipients] = useState(false);
  const [preview, setPreview] = useState(null); // { subject, html }
  const [previewLoading, setPreviewLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [csvText, setCsvText] = useState('');
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  const template = TEMPLATES.find(t => t.key === selectedKey);

  // ─── Fetch backend status on mount
  useEffect(() => {
    axios.get('/api/v1/email-campaigns/status')
      .then(r => setStatus(r.data))
      .catch(() => {});
  }, []);

  // ─── Load recipients when template changes
  useEffect(() => {
    if (!template) return;
    setRecipients([]);
    setRecipientVarOverrides({});
    setGlobalVars({});
    setSendResult(null);
    setPreview(null);
    setCsvText('');

    if (!template.manualRecipients) {
      setLoadingRecipients(true);
      axios.get(`/api/v1/email-campaigns/recipients/${template.recipientType}`)
        .then(r => setRecipients(r.data || []))
        .catch(() => setRecipients([]))
        .finally(() => setLoadingRecipients(false));
    }
  }, [selectedKey]);

  const handleGlobalVar = (name, value) => setGlobalVars(prev => ({ ...prev, [name]: value }));

  const handleRecipientVarChange = (idx, name, value) =>
    setRecipientVarOverrides(prev => ({ ...prev, [idx]: { ...(prev[idx] || {}), [name]: value } }));

  const handleRemoveRecipient = (idx) =>
    setRecipients(prev => prev.filter((_, i) => i !== idx));

  const handleCSVLoad = () => {
    const parsed = parseProspectCSV(csvText);
    if (parsed.length === 0) return;
    setRecipients(parsed);
    setCsvText('');
  };

  const buildPreviewVars = useCallback(() => {
    const first = recipients[0] || {};
    return { ...first, ...globalVars };
  }, [recipients, globalVars]);

  const handlePreview = async () => {
    if (!template) return;
    setPreviewLoading(true);
    try {
      const r = await axios.post('/api/v1/email-campaigns/preview', {
        templateKey: template.key,
        vars: buildPreviewVars(),
      });
      setPreview(r.data);
      setShowPreviewModal(true);
    } catch {
      setPreview({ subject: 'Error', html: '<p>Could not render preview.</p>' });
      setShowPreviewModal(true);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSend = async () => {
    if (!template || recipients.length === 0) return;
    setSending(true);
    setSendResult(null);
    try {
      const enrichedRecipients = recipients.map((r, idx) => ({
        ...r,
        ...(recipientVarOverrides[idx] || {}),
      }));
      const r = await axios.post('/api/v1/email-campaigns/send', {
        templateKey: template.key,
        recipients: enrichedRecipients,
        globalVars,
      });
      setSendResult(r.data);
    } catch (err) {
      setSendResult({ error: err.response?.data?.error || 'Send failed' });
    } finally {
      setSending(false);
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Email Campaigns</h1>
          <p className="text-sm text-gray-400 mt-0.5">9 templates · Nodemailer + Gmail SMTP</p>
        </div>
        {status && (
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium border ${
            status.dryRun
              ? 'bg-amber-500/10 text-amber-300 border-amber-500/30'
              : 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30'
          }`}>
            <span className={`w-2 h-2 rounded-full ${status.dryRun ? 'bg-amber-400' : 'bg-emerald-400'}`} />
            {status.dryRun ? 'Dry-run — no emails sent' : 'Live — emails will send'}
          </div>
        )}
      </div>

      {status?.dryRun && (
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 text-sm text-amber-200">
          <strong>Dry-run mode is active.</strong> All sends will be logged to the server console but no emails will be delivered.
          Set <code className="bg-black/30 px-1 rounded">EMAIL_CAMPAIGNS_ENABLED=true</code> in your server .env to go live.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* ── Left: Template picker ── */}
        <div className="space-y-2">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-1">Templates</p>
          {TEMPLATES.map(t => (
            <button
              key={t.key}
              onClick={() => setSelectedKey(t.key)}
              className={`w-full text-left px-4 py-3 rounded-xl border transition-all ${
                selectedKey === t.key
                  ? 'bg-blue-600/20 border-blue-500/40 text-white'
                  : 'bg-white/3 border-white/8 text-gray-300 hover:bg-white/6 hover:text-white'
              }`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium leading-tight">{t.label}</span>
                <Tag color={t.tagColor} label={t.tag} />
              </div>
              <p className="text-xs text-gray-500">{t.trigger}</p>
            </button>
          ))}
        </div>

        {/* ── Right: Campaign builder ── */}
        <div className="lg:col-span-2 space-y-5">
          {!template ? (
            <div className="flex flex-col items-center justify-center h-64 rounded-2xl border border-white/8 bg-white/2 text-gray-500">
              <svg className="w-12 h-12 mb-3 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <p className="text-sm">Select a template to configure</p>
            </div>
          ) : (
            <>
              {/* Template info */}
              <div className="bg-white/3 border border-white/8 rounded-2xl p-5">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div>
                    <h2 className="text-lg font-semibold text-white">{template.label}</h2>
                    <p className="text-sm text-gray-400 mt-0.5">{template.description}</p>
                  </div>
                  <Tag color={template.tagColor} label={template.tag} />
                </div>
                <div className="flex gap-3 mt-3 text-xs text-gray-500">
                  <span>🕐 {template.trigger}</span>
                  <span>·</span>
                  <span>📧 hello@teamgrid.ai</span>
                </div>
              </div>

              {/* Global vars */}
              {template.globalVars.length > 0 && (
                <div className="bg-white/3 border border-white/8 rounded-2xl p-5 space-y-4">
                  <h3 className="text-sm font-semibold text-white">Campaign Variables</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {template.globalVars.map(v => (
                      <InputField
                        key={v.name}
                        label={v.label}
                        name={v.name}
                        value={globalVars[v.name] || ''}
                        onChange={handleGlobalVar}
                        placeholder={v.placeholder}
                        required={v.required}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Recipients */}
              <div className="bg-white/3 border border-white/8 rounded-2xl p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-white">
                    Recipients
                    {recipients.length > 0 && (
                      <span className="ml-2 px-2 py-0.5 bg-blue-500/20 text-blue-300 text-xs rounded-full">
                        {recipients.length}
                      </span>
                    )}
                  </h3>
                  {!template.manualRecipients && (
                    <button
                      onClick={() => {
                        setLoadingRecipients(true);
                        axios.get(`/api/v1/email-campaigns/recipients/${template.recipientType}`)
                          .then(r => setRecipients(r.data || []))
                          .catch(() => {})
                          .finally(() => setLoadingRecipients(false));
                      }}
                      className="text-xs text-blue-400 hover:text-blue-300 transition-colors"
                    >
                      ↺ Refresh
                    </button>
                  )}
                </div>

                {/* Manual CSV input for cold/winback */}
                {template.manualRecipients && (
                  <div className="space-y-2">
                    <p className="text-xs text-gray-500">Paste CSV: <code className="bg-white/5 px-1 rounded">email, name, companyName</code> (one per line)</p>
                    <textarea
                      value={csvText}
                      onChange={e => setCsvText(e.target.value)}
                      placeholder={`john@acme.com, John Doe, Acme Corp\njane@globex.com, Jane Smith, Globex`}
                      rows={4}
                      className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder-gray-600 font-mono resize-none focus:outline-none focus:border-blue-500/50"
                    />
                    <button
                      onClick={handleCSVLoad}
                      disabled={!csvText.trim()}
                      className="px-3 py-1.5 bg-blue-600/30 hover:bg-blue-600/50 disabled:opacity-40 text-blue-200 text-xs rounded-lg border border-blue-500/30 transition-all"
                    >
                      Load {parseProspectCSV(csvText).length || ''} recipients
                    </button>
                  </div>
                )}

                {loadingRecipients ? (
                  <div className="space-y-2">
                    {[1, 2, 3].map(i => (
                      <div key={i} className="h-14 bg-white/5 rounded-lg animate-pulse" />
                    ))}
                  </div>
                ) : recipients.length === 0 ? (
                  <div className="text-center py-8 text-gray-600 text-sm">
                    {template.manualRecipients ? 'Paste CSV above and click Load' : 'No recipients found for this campaign right now.'}
                  </div>
                ) : (
                  <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                    {recipients.map((r, idx) => (
                      <RecipientRow
                        key={idx}
                        r={r}
                        idx={idx}
                        vars={recipientVarOverrides}
                        onVarChange={handleRecipientVarChange}
                        onRemove={handleRemoveRecipient}
                        recipientVars={template.recipientVars}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Action bar */}
              <div className="flex items-center gap-3">
                <button
                  onClick={handlePreview}
                  disabled={previewLoading || recipients.length === 0}
                  className="flex items-center gap-2 px-4 py-2.5 bg-white/5 hover:bg-white/10 disabled:opacity-40 text-white text-sm rounded-xl border border-white/10 transition-all"
                >
                  {previewLoading ? (
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                  Preview Email
                </button>

                <button
                  onClick={handleSend}
                  disabled={sending || recipients.length === 0}
                  className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-40 text-white text-sm font-medium rounded-xl transition-all"
                >
                  {sending ? (
                    <>
                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                      </svg>
                      Sending…
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                      </svg>
                      {status?.dryRun ? 'Dry-run Send' : `Send to ${recipients.length}`}
                    </>
                  )}
                </button>

                {sendResult && !sendResult.error && (
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-emerald-400">✓ {sendResult.sent} sent</span>
                    {sendResult.failed > 0 && <span className="text-red-400">· {sendResult.failed} failed</span>}
                    {sendResult.dryRun && <span className="text-amber-400">(dry-run)</span>}
                  </div>
                )}
                {sendResult?.error && (
                  <span className="text-red-400 text-sm">✕ {sendResult.error}</span>
                )}
              </div>

              {/* Send result details */}
              {sendResult?.results && sendResult.results.length > 0 && (
                <div className="bg-white/3 border border-white/8 rounded-2xl p-4 space-y-1 max-h-48 overflow-y-auto">
                  <p className="text-xs font-semibold text-gray-400 mb-2">Send Log</p>
                  {sendResult.results.map((r, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs">
                      <span className={r.ok ? 'text-emerald-400' : 'text-red-400'}>{r.ok ? '✓' : '✕'}</span>
                      <span className="text-gray-300">{r.email}</span>
                      {r.dryRun && <span className="text-amber-500">[dry-run]</span>}
                      {r.error && <span className="text-red-400">— {r.error}</span>}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Preview Modal ── */}
      {showPreviewModal && preview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="bg-gray-900 border border-white/10 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/8">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-0.5">Preview</p>
                <p className="text-sm font-semibold text-white">{preview.subject}</p>
              </div>
              <button
                onClick={() => setShowPreviewModal(false)}
                className="p-2 text-gray-400 hover:text-white transition-colors rounded-lg hover:bg-white/10"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5">
              <div className="bg-white rounded-xl p-6">
                <div
                  className="text-gray-800 text-sm leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: preview.html }}
                />
              </div>
            </div>
            <div className="px-5 py-3 border-t border-white/8 flex justify-end">
              <button
                onClick={() => setShowPreviewModal(false)}
                className="px-4 py-2 bg-white/10 hover:bg-white/15 text-white text-sm rounded-lg transition-all"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
